import { Client, Room } from 'colyseus.js';
import type { ClientMessage, ServerMessage } from '@shared/types/messages';

export type GameRoom = Room;

export class ColyseusClient {
  private client: Client;
  private room: GameRoom | null = null;
  private statusRoom: GameRoom | null = null;

  constructor(serverUrl: string) {
    this.client = new Client(serverUrl);
  }

  async joinGameRoom(options: unknown): Promise<GameRoom> {
    this.room = await this.client.joinOrCreate('game', options);
    return this.room;
  }

  /**
   * Sale de la room actual (si hay) y se une a una nueva con `options`.
   * Usado por translado cross-map: el cliente abandona la GameRoom del mapa actual
   * y se conecta a la del mapa destino. La posición ya quedó persistida en DB
   * por el server con saveCharacterMapAndPosition antes de mandar PLAYER_MAP_CHANGE.
   */
  async leaveAndRejoin(options: unknown): Promise<GameRoom> {
    if (this.room) {
      try {
        await this.room.leave(true);
      } catch {
        // best-effort: si el leave falla (WS ya cerrado), seguimos al rejoin
      }
      this.room = null;
    }
    return this.joinGameRoom(options);
  }

  async joinStatusRoom(): Promise<GameRoom> {
    // Conectarse a la room de estado (joinOrCreate crea una si no existe)
    // Todos los clientes usarán la misma instancia ya que no filtramos por opciones
    this.statusRoom = await this.client.joinOrCreate('status', {});
    return this.statusRoom;
  }

  leaveStatusRoom(): void {
    if (this.statusRoom) {
      this.statusRoom.leave();
      this.statusRoom = null;
    }
  }

  getRoom(): GameRoom | null {
    return this.room;
  }

  getStatusRoom(): GameRoom | null {
    return this.statusRoom;
  }

  send(message: ClientMessage): void {
    if (!this.room) return;
    const anyRoom = this.room as any;
    const ws = anyRoom.connection?.transport?.ws as WebSocket | undefined;
    if (ws && ws.readyState !== WebSocket.OPEN) {
      // console warning once
      // eslint-disable-next-line no-console
      console.warn('[ColyseusClient] Room WebSocket not open, skipping send.');
      return;
    }

    // Enviamos tipo + payload, siguiendo la convención de Colyseus
    try {
      this.room.send(message.type, message);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ColyseusClient] Failed to send message:', error);
    }
  }

  onServerMessage(handler: (message: ServerMessage) => void): void {
    if (!this.room) return;
    this.room.onMessage('*', (type, message) => {
      // El servidor envía mensajes tipados (ServerMessageType)
      const payload = (message ?? {}) as ServerMessage;
      // Asegurarnos de pasar el tipo correcto al handler
      handler({ ...(payload as object), type } as ServerMessage);
    });
  }

  onStatusMessage(handler: (message: ServerMessage) => void): void {
    if (!this.statusRoom) return;
    this.statusRoom.onMessage('*', (type, message) => {
      // El servidor envía mensajes tipados (ServerMessageType)
      const payload = (message ?? {}) as ServerMessage;
      // Asegurarnos de pasar el tipo correcto al handler
      handler({ ...(payload as object), type } as ServerMessage);
    });
  }

  onStatusRoomDisconnect(handler: () => void): void {
    if (!this.statusRoom) return;
    this.statusRoom.onLeave(() => {
      handler();
    });
    this.statusRoom.onError(() => {
      handler();
    });
  }

  isConnected(): boolean {
    if (!this.room) return false;
    const anyRoom = this.room as any;
    const ws = anyRoom.connection?.transport?.ws as WebSocket | undefined;
    return ws !== undefined && ws.readyState === WebSocket.OPEN;
  }
}


